import type { CustomAppInfo } from '@affine/core/modules/streamlain/hooks/use-workspace-custom-apps';

export interface ManagedDatabaseOwner {
  label: string;
  openLabel: string;
  open: () => void;
}

export interface ManagedDatabaseProtectionInput {
  schemaLocked?: boolean;
  systemManaged?: boolean;
  managedBy?: string;
}

export function readManagedDatabaseProtectionFromModel(
  model: unknown
): ManagedDatabaseProtectionInput {
  const props = ((model as { props?: Record<string, unknown> } | null)?.props ??
    {}) as Record<string, unknown>;
  const storageProps = (((
    model as { dataSource?: { storageModel?: { props?: unknown } } } | null
  )?.dataSource?.storageModel?.props as Record<string, unknown> | undefined) ??
    {}) as Record<string, unknown>;
  return {
    schemaLocked:
      props.schemaLocked === true || storageProps.schemaLocked === true,
    systemManaged:
      props.systemManaged === true || storageProps.systemManaged === true,
    managedBy:
      typeof storageProps.managedBy === 'string'
        ? storageProps.managedBy
        : typeof props.managedBy === 'string'
          ? props.managedBy
          : undefined,
  };
}

export function isManagedDatabaseProtected(
  input: ManagedDatabaseProtectionInput | null | undefined
): boolean {
  return Boolean(
    input?.schemaLocked === true ||
    input?.systemManaged === true ||
    input?.managedBy
  );
}

export function resolveCustomAppDatabaseOwner(input: {
  blockId: string;
  docId: string;
  databaseId?: string | null;
  customApps: CustomAppInfo[] | undefined;
  openCustomApp: (customAppId: string) => void;
}): ManagedDatabaseOwner | null {
  for (const customApp of input.customApps ?? []) {
    if (!customAppConfigContainsDatabase(customApp.config, input)) continue;
    return {
      label: `Custom App "${customApp.title}"`,
      openLabel: 'Custom App öffnen',
      open: () => input.openCustomApp(customApp.id),
    };
  }
  return null;
}

export function describeManagedDatabaseOwner(input: {
  managedBy?: string;
  fallbackDocTitle?: string;
  explicitOwner?: ManagedDatabaseOwner | null;
  openSourceDoc?: (() => void) | null;
}): ManagedDatabaseOwner | null {
  if (input.explicitOwner) return input.explicitOwner;
  if (!input.managedBy && !input.openSourceDoc) return null;

  const label = input.managedBy
    ? `System "${input.managedBy}"`
    : input.fallbackDocTitle
      ? `Dokument "${input.fallbackDocTitle}"`
      : 'System';

  return {
    label,
    openLabel: 'Quelle öffnen',
    open: () => input.openSourceDoc?.(),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function customAppConfigContainsDatabase(
  config: unknown,
  input: { blockId: string; docId: string; databaseId?: string | null }
): boolean {
  const databaseBindings = asRecord(asRecord(config)?.databaseBindings);
  if (!databaseBindings) return false;

  for (const value of Object.values(databaseBindings)) {
    const binding = asRecord(value);
    const placement = asRecord(binding?.placement);
    if (!binding || !placement) continue;
    if (binding.status !== undefined && binding.status !== 'active') continue;
    if (placement.docId !== input.docId) continue;
    if (placement.blockId === input.blockId) return true;
    if (input.databaseId && binding.databaseId === input.databaseId) {
      return true;
    }
  }
  return false;
}
